import SchemaValidator from "./schema-validator";
import jwtDecode from "jwt-decode";
import EventEmitter from "events";

export type ApiClientPlatformConfig = {
	readonly verityOrigin: string;
	readonly lockdown: {
		origin: string;
		channel: string;
		cdnBaseUrl: string;
	};
};

export type ApiClientImplementationConfig = {
	getRefreshToken: () => Promise<string | undefined>;
	setRefreshToken: (refreshToken: string | undefined) => Promise<void>;

	getAccountId: () => Promise<string | undefined>;
	setAccountId: (accountId: string | undefined) => Promise<void>;

	getAccessToken: () => Promise<string | undefined>;
	setAccessToken: (accessToken: string | undefined) => Promise<void>;

	getLocalRefreshedTime: () => Promise<number | undefined>;
	setLocalRefreshedTime: (timestamp: number | undefined) => Promise<void>;

	isEnvironmentSupported: () => boolean;
};

export type ApiClientConfig = ApiClientImplementationConfig & ApiClientPlatformConfig;

export type Jwt = {
	sub?: string;
	uid?: string;
	exp?: number;
	jti?: string;
};

export abstract class ApiClient<SV extends Record<string, SchemaValidator> = Record<string, never>> {
	public readonly config;
	readonly schemaValidators;
	readonly emitter: EventEmitter;

	/* Shared token state */
	private static tokenLock: Promise<boolean> | null;
	private static tokenUnlock: (value: unknown) => void;
	private static tokenTimeOut: NodeJS.Timeout | null;
	private static tokenUseBuffer: Promise<boolean> | null;
	private static callId: number;
	private static debug: boolean;
	private static apiRouteConfig: Array<{
		route: string;
		version: string;
	}> | null;

	/**
	 * Maps a pair of accountId and refreshToken to a boolean indicating whether the token is valid.
	 *
	 * The key is formatted as: "<accountId>:<refreshToken>"
	 */
	private static isRefreshPayloadValidMap = new Map<string, boolean>();

	constructor(config: ApiClientConfig, schemaValidators: SV) {
		this.config = config;
		this.schemaValidators = schemaValidators;
		this.emitter = new EventEmitter();
		ApiClient.tokenLock = null;
		ApiClient.tokenUnlock = () => {};
		ApiClient.tokenTimeOut = null;
		ApiClient.tokenUseBuffer = null;
		ApiClient.callId = 0;
		ApiClient.debug = false;
		ApiClient.apiRouteConfig = null;
	}

	/* ----------------------------------------------
		Debug Helper Functions
	----------------------------------------------*/

	private log(msg: string, ...args: unknown[]) {
		if (ApiClient.debug) {
			console.error(`[API-CLIENT] ${msg}`, ...args);
		}
	}

	/* ----------------------------------------------
		Wrappers for config getters and setters
	 ----------------------------------------------*/

	async setAccessToken(this: ApiClient<SV>, accessToken: string | undefined): Promise<void> {
		this.log(`Setting access token to ${accessToken}`);

		await this.config.setAccessToken(accessToken);
		this.emitter.emit("access-token-updated", accessToken ? jwtDecode<Jwt>(accessToken) : undefined);
		void this.updateApiRouteConfig();
		this.log(`Set access token to ${accessToken}`);
		return;
	}

	async getAccessToken(this: ApiClient<SV>): Promise<string | undefined> {
		this.log(`Getting access token`);
		const accessToken = await this.config.getAccessToken();
		this.log(`Got access token ${accessToken}`);
		return accessToken;
	}

	async setRefreshToken(this: ApiClient<SV>, refreshToken: string | undefined): Promise<void> {
		this.log(`Setting refresh token to ${refreshToken}`);
		await this.config.setRefreshToken(refreshToken);
		this.log(`Set refresh token to ${refreshToken}`);
		return;
	}

	async getRefreshToken(this: ApiClient<SV>): Promise<string | undefined> {
		this.log(`Getting refresh token`);
		const refreshToken = await this.config.getRefreshToken();
		this.log(`Got refresh token ${refreshToken}`);
		return refreshToken;
	}

	async setAccountId(this: ApiClient<SV>, accountId: string | undefined): Promise<void> {
		this.log(`Setting account id to ${accountId}`);
		await this.config.setAccountId(accountId);
		this.log(`Set account id to ${accountId}`);
		return;
	}

	async getAccountId(this: ApiClient<SV>): Promise<string | undefined> {
		this.log(`Getting account id`);
		const accountId = await this.config.getAccountId();
		this.log(`Got account id ${accountId}`);
		return accountId;
	}

	async getLocalRefreshedTime(this: ApiClient<SV>): Promise<number | undefined> {
		this.log(`Getting local refreshed time`);
		const timestamp = await this.config.getLocalRefreshedTime();
		this.log(`Got local refreshed time ${timestamp}`);
		return timestamp;
	}

	async setLocalRefreshedTime(this: ApiClient<SV>, timestamp: number | undefined): Promise<void> {
		this.log(`Setting local refreshed time to ${timestamp}`);
		await this.config.setLocalRefreshedTime(timestamp);
		this.log(`Set local refreshed time to ${timestamp}`);
		return;
	}

	/**
	 * Use the refresh token to update the access and refresh tokens.
	 *
	 * If another updateTokens is in progress, a new call will wait for the original to finish.
	 */
	async updateTokens(this: ApiClient<SV>) {
		const myId = ApiClient.callId++;
		this.log(`[Call ${myId}] Updating tokens`);

		/* If another updateTokens is in progress, wait for it to finish */
		if (ApiClient.tokenLock) {
			this.log(`[Call ${myId}] Waiting for another updateTokens to finish`);

			/* Wait for the other thread to finish */
			await ApiClient.tokenLock;

			this.log(`[Call ${myId}] Another updateTokens finished`);
			return;
		} else {
			/* Otherwise, create a new lock and wait for it to finish */
			this.log(`[Call ${myId}] Setting new token lock`);
			ApiClient.tokenLock = new Promise((resolve) => {
				ApiClient.tokenUnlock = () => {
					resolve(true);
					ApiClient.tokenLock = null;
				};

				ApiClient.tokenTimeOut = setTimeout(() => {
					resolve(false);
					ApiClient.tokenLock = null;
				}, 10000);
			});
		}

		/* Can Assume Atomic Execution of Following Code */
		try {
			/* If another updateTokens recently finished, wait for the between call buffer */
			if (ApiClient.tokenUseBuffer) {
				this.log(`[Call ${myId}] Waiting for token buffer to finish`);

				/* Wait for the other thread to finish */
				await ApiClient.tokenUseBuffer;

				this.log(`[Call ${myId}] Token buffer finished`);
				return;
			}

			/* Assert accountId and refreshToken are set */
			const currentAccountId = await this.getAccountId();
			const currentRefreshToken = await this.getRefreshToken();
			if (!currentAccountId || !currentRefreshToken) {
				this.log(`[Call ${myId}] No accountId or refreshToken, not updating tokens`);
				return;
			}

			/* Check if the refresh payload is valid */
			const isRefreshPayloadValidKey = `${currentAccountId}:${currentRefreshToken}`;
			const isRefreshPayloadValid = ApiClient.isRefreshPayloadValidMap.get(isRefreshPayloadValidKey);

			if (isRefreshPayloadValid !== undefined && !isRefreshPayloadValid) {
				this.log(`[Call ${myId}] Refresh payload is invalid, not updating tokens`);
				return;
			}

			/* Refresh access and refresh tokens */
			const response = await fetch(`${this.config.verityOrigin}/api/access-control/refresh`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					accountId: currentAccountId,
					refreshToken: currentRefreshToken,
				}),
			});

			/* Emit refresh on response */
			this.emitter.emit("refresh", response);
			this.log(`[Call ${myId}] Response received from refresh`, response);

			/* If refresh is successful, set tokens */
			if (response.ok) {
				const { accessToken, refreshToken } = (await response.json()) as {
					accessToken?: unknown;
					refreshToken?: unknown;
				};
				if (typeof accessToken === "string") {
					await this.setAccessToken(accessToken);
				}
				if (typeof refreshToken === "string") {
					await this.setRefreshToken(refreshToken);
				}
				await this.setLocalRefreshedTime(Date.now());
			} else {
				/* If invalid, set isRefreshPayloadValid to false to prevent unnecessary retries */
				this.log(`[Call ${myId}] Response not ok from refresh`, response);
				ApiClient.isRefreshPayloadValidMap.set(isRefreshPayloadValidKey, false);
			}
		} catch (e) {
			this.log(`[Call ${myId}] Error updating tokens`, e);
		} finally {
			/* Clear the execution timeout */
			this.log(`[Call ${myId}] Clearing token timeout`);
			if (ApiClient.tokenTimeOut) {
				clearTimeout(ApiClient.tokenTimeOut);
			}

			/* Free Lock */
			this.log(`[Call ${myId}] Freeing token lock`);
			ApiClient.tokenUnlock?.(false);

			/* Create the buffer */
			this.log(`[Call ${myId}] Creating token buffer`);
			ApiClient.tokenUseBuffer = new Promise((resolve) => {
				setTimeout(() => {
					resolve(true);
					ApiClient.tokenUseBuffer = null;
				}, 100);
			});

			/* Call the original callback */
			this.log(`[Call ${myId}] Finished token update`);
		}
	}

	/**
	 * Returns the JWT payload of the current access token, if available.
	 * @returns {Jwt | undefined} The JWT payload of the current access token, or undefined if not available.
	 */
	async getJwt(this: ApiClient<SV>) {
		this.log(`Getting JWT`);
		const currentAccessToken = await this.getAccessToken();
		if (!currentAccessToken) {
			return undefined;
		}
		const jwt = jwtDecode<Jwt>(currentAccessToken);
		this.log(`Got JWT`, jwt);
		return jwt;
	}

	/**
	 * Sets the access token to null and ends the current session.
	 */
	async resetAccessToken(this: ApiClient<SV>) {
		this.log(`Resetting access token`);
		await this.setAccessToken(undefined);
		await fetch(`${this.config.verityOrigin}/api/verity/session/end`, { credentials: "include" });
		this.emitter.emit("access-token-updated", undefined);
		this.log(`Reset access token`);
	}

	/**
	 * Get the authorization header using the current access token
	 */
	async generateAuthorizationHeader(this: ApiClient<SV>) {
		this.log(`Generating authorization header`);
		const accessToken = await this.getAccessToken();
		this.log(`Got authorization header`, { Authorization: `Bearer ${accessToken}` });
		return { Authorization: `Bearer ${accessToken}` };
	}

	/**
	 * Mutate the fetch parameters to include the authorization header
	 */
	async applyAuthenticatedFetchParams(
		this: ApiClient<SV>,
		...params: Parameters<typeof fetch>
	): Promise<Parameters<typeof fetch>> {
		this.log(`Generating authenticated fetch parameters`);
		const [originalInput, originalInit] = params;

		if (!(await this.getAccessToken())) {
			return [originalInput, originalInit];
		}

		try {
			const authHeader = await this.generateAuthorizationHeader();

			if (originalInput instanceof Request && !originalInit?.headers) {
				Object.entries(authHeader).forEach(([key, value]) => originalInput.headers.set(key, value));
				this.log(`Generated authenticated fetch parameters`, { originalInput, originalInit, authHeader });
				return [originalInput, originalInit];
			} else {
				const modifiedInit = {
					...originalInit,
					headers: {
						...originalInit?.headers,
						...authHeader,
					},
				};

				this.log(`Generated authenticated fetch parameters`, { originalInput, originalInit, authHeader, modifiedInit });
				return [originalInput, modifiedInit];
			}
		} catch (error) {
			this.log(`Failed to generate authenticated fetch parameters`, error);
			return [originalInput, originalInit];
		}
	}

	/**
	 * Update the API route config for this instance
	 */
	async updateApiRouteConfig(this: ApiClient<SV>) {
		try {
			this.log(`Updating API route config`);
			const response = await fetch(`${this.config.verityOrigin}/api/verity/version/config`, {
				headers: {
					...(await this.generateAuthorizationHeader()),
				},
			});
			if (!response.ok) {
				throw new Error("Failed to update API route config");
			}
			const data: unknown = await response.json();
			if (typeof data !== "object" || !data) {
				throw new Error("Invalid API route config");
			}
			const routeConfig = data as Array<{ route: string; version: string }>;
			ApiClient.apiRouteConfig = routeConfig;
			this.log(`Updated API route config`, routeConfig);
		} catch (error) {
			ApiClient.apiRouteConfig = null;
			this.log(`Failed to update API route config`, error);
		}
	}

	/**
	 * Use the API route config for this instance. Mutates the fetch parameters to include the API version header
	 */
	applyApiRouteConfig(this: ApiClient<SV>, ...params: Parameters<typeof fetch>): Parameters<typeof fetch> {
		const [originalInput, originalInit] = params;
		try {
			this.log(`Using API route config`);
			if (!ApiClient.apiRouteConfig) {
				return [originalInput, originalInit];
			}

			const targetUrl =
				originalInput instanceof Request
					? originalInput.url
					: originalInput instanceof URL
						? originalInput.toString()
						: originalInput;

			for (const { route, version } of ApiClient.apiRouteConfig) {
				if (targetUrl.includes(route)) {
					this.log(`Using API route config for route ${route}`);

					if (originalInput instanceof Request && !originalInit?.headers) {
						Object.entries({ "X-Api-Version": version }).forEach(([key, value]) =>
							originalInput.headers.set(key, value),
						);
						this.log(`Generated API route config`, {
							originalInput,
							originalInit,
							version,
							headers: originalInput.headers,
						});
						return [originalInput, originalInit];
					} else {
						const modifiedInit = {
							...originalInit,
							headers: {
								...originalInit?.headers,
								"X-Api-Version": version,
							},
						};
						this.log(`Generated API route config`, {
							originalInput,
							originalInit,
							version,
							headers: modifiedInit.headers,
						});
						return [originalInput, modifiedInit];
					}
				}
			}

			this.log(`No API route config found for route ${targetUrl}`);
			return [originalInput, originalInit];
		} catch (error) {
			this.log(`Failed to use API route config`, error);
			return [originalInput, originalInit];
		}
	}

	/**
	 * Get the current route version for a specific route
	 */
	getRouteVersion(this: ApiClient<SV>, route: string): string {
		if (!ApiClient.apiRouteConfig) {
			return "v1";
		}

		return ApiClient.apiRouteConfig.find((config) => route.includes(config.route))?.version ?? "v1";
	}

	/**
	 * Apply fetch overrides for this instance. Mutates the fetch parameters to include the authorization header and API version header
	 */
	async applyFetchOverrides(
		this: ApiClient<SV>,
		...params: Parameters<typeof fetch>
	): Promise<Parameters<typeof fetch>> {
		const [originalInput, originalInit] = params;
		try {
			const [authInput, authInit] = await this.applyAuthenticatedFetchParams(originalInput, originalInit);
			const [versionedInput, versionedInit] = this.applyApiRouteConfig(authInput, authInit);
			this.log(`Applying fetch overrides`, { input: versionedInput, init: versionedInit });
			return [versionedInput, versionedInit];
		} catch (error) {
			this.log(`Failed to apply fetch overrides`, error);
			return [originalInput, originalInit];
		}
	}

	private fetchPromise: Promise<Response> | undefined;

	/**
	 * Clones the fetch parameters to prevent Request objects from being reused
	 */
	private cloneFetchParams(this: ApiClient<SV>, ...params: Parameters<typeof fetch>): Parameters<typeof fetch> {
		const [input, init] = params;

		if (input instanceof Request) {
			return [new Request(input), init ? { ...init } : undefined];
		}
		return [input, init ? { ...init } : undefined];
	}

	/**
	 * Wrapper around fetch that applies overrides
	 */
	async fetch(this: ApiClient<SV>, ...params: Parameters<typeof fetch>) {
		this.log(`Fetching`, params);
		if (this.fetchPromise) {
			await this.fetchPromise;
		}

		this.fetchPromise = (async () => {
			const response = await fetch(...(await this.applyFetchOverrides(...this.cloneFetchParams(...params))));
			this.log(`Fetch response`, response);
			if (response.status === 401 || response.status === 403) {
				this.log(`Token expired, updating tokens`);
				await this.updateTokens();
				this.log(`Token updated, retrying fetch`);
				const [retryInput, retryInit] = await this.applyFetchOverrides(...this.cloneFetchParams(...params));
				return await fetch(retryInput, retryInit);
			}
			this.fetchPromise = undefined;
			return response;
		})();
		return await this.fetchPromise;
	}

	/**
	 * Clear all cookies in the current document
	 */
	clearCookies(this: ApiClient<SV>) {
		if (document && document.cookie) {
			const cookies = document.cookie.split(";");
			cookies.forEach((cookie) => {
				const [name] = cookie.split("=");
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
			});
		}
	}
}
